import type { ReactNode } from 'react';
import type { RichTextField } from '@prismicio/client';
import { asText, isFilled } from '@prismicio/client';
import { PrismicRichText } from '@prismicio/react';
import { cn } from '@/lib/utils';

export { StatementBox } from './StatementBox';
export { OpenLinkIcon } from './OpenLinkIcon';

type Theme = 'light' | 'dark' | null;

export type StackedHeaderStatementBoxesProps = {
  sliceType: string;
  variation: string;
  theme?: Theme;
  /** Muted / first-tone half of the heading. */
  title?: RichTextField | null;
  /** Dark / second-tone half of the heading. */
  subtitle?: RichTextField | null;
  /** Supporting paragraph(s) below the heading. */
  description?: RichTextField | null;
  removeUpperMargin?: boolean;
  children: ReactNode;
};

/**
 * Section shell for the "stacked header + statement boxes" family: the same
 * two-tone stacked heading used by `StackedHeaderCardsRow` sits on top with
 * an optional supporting description, and the vertical stack of statement
 * boxes fills the body.
 */
export function StackedHeaderStatementBoxes({
  sliceType,
  variation,
  theme,
  title,
  subtitle,
  description,
  removeUpperMargin,
  children,
}: StackedHeaderStatementBoxesProps) {
  const isDark = theme === 'dark';
  const hasTitle = isFilled.richText(title);
  const hasSubtitle = isFilled.richText(subtitle);
  const hasDescription = isFilled.richText(description);

  return (
    <section
      data-slice-type={sliceType}
      data-slice-variation={variation}
      className={cn(
        'px-5 md:px-[30px]',
        removeUpperMargin ? 'pt-0' : 'pt-[70px]',
        isDark ? 'bg-[#262626] text-white' : 'bg-white text-[#041118]',
      )}
    >
      <div className="mx-auto flex w-full max-w-[1280px] flex-col items-start gap-8 pb-[70px] md:gap-9">
        {(hasTitle || hasSubtitle || hasDescription) && (
          <div className="flex w-full flex-col gap-4">
            {(hasTitle || hasSubtitle) && (
              <h2 className="w-full text-balance break-words text-[28px] font-medium leading-[1.2] md:text-[42px]">
                {hasTitle && (
                  <span className={isDark ? 'text-[#05f273]' : 'text-[#aaa]'}>
                    {asText(title)}
                  </span>
                )}
                {hasTitle && hasSubtitle && <br />}
                {hasSubtitle && (
                  <span className={isDark ? 'text-white' : 'text-[#041118]'}>
                    {asText(subtitle)}
                  </span>
                )}
              </h2>
            )}
            {hasDescription && (
              <PrismicRichText
                field={description}
                components={{
                  paragraph: ({ children: c }) => (
                    <p
                      className={cn(
                        'text-[16px] font-light leading-[1.5] md:text-[18px] [&:not(:first-child)]:mt-4',
                        isDark ? 'text-white/75' : 'text-[#041118]',
                      )}
                    >
                      {c}
                    </p>
                  ),
                  strong: ({ children: c }) => (
                    <strong className="font-semibold">{c}</strong>
                  ),
                  hyperlink: ({ children: c, node }) => (
                    <a
                      href={node.data.url ?? '#'}
                      className="text-[#4b0fea] underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {c}
                    </a>
                  ),
                }}
              />
            )}
          </div>
        )}

        <div className="flex w-full flex-col gap-4 md:gap-5">{children}</div>
      </div>
    </section>
  );
}
